import moment from "moment";

import { Client } from "@/Common/Api/Client";
import { BaseReactComponent } from "@/Common/Components/BaseReactComponent";
import { CheckedListControl } from "@/Common/Components/Inputs/CheckedListControl";
import { Modal } from "@/Common/Components/Modals/Modal";
import { PrintHelper } from "@/Common/Helpers/PrintHelper";
import { CheckboxUIModelT } from "@/Common/Models/UI/Inputs/CheckboxUIModel";
import * as ServerOperations from "@/Common/Operations/operations.server";
import { PrintFetchUrlResponse } from "@/ExportableFile/Models/PrintFetchUrlResponse";
import { RadPdfExportableFileHelper } from "@/Patient/Helpers/RadPdfExportableFileHelper";
import { PatientForms } from "@/Patient/Models/Forms/RadPdf/Forms";
import { ObstetricalFileAction } from "./Actions/ObstetricalFileAction";

interface IPreviousObstetricalPdfFormPopupProps {
    encounterId: string;
    patientContactFacetId: string;
    onContinue: () => void;
    onCancel: () => void;
}

class PreviousObstetricalPdfFormPopupState {
    public checkablePatientForms: CheckboxUIModelT<PatientForms>[];
}

class PreviousObstetricalPdfFormPopup extends BaseReactComponent<IPreviousObstetricalPdfFormPopupProps, PreviousObstetricalPdfFormPopupState> {
    private action: ObstetricalFileAction;

    public componentDidMount(): void {
        this.action = new ObstetricalFileAction();

        this.action.getPreviousObstetricalFilePdfForms(this.props.encounterId, this.props.patientContactFacetId)
            .then(response => this.mutateState(s => {
                s.checkablePatientForms = response.PatientForms?.map(x => {
                    let checkableModel = new CheckboxUIModelT<PatientForms>();
                    checkableModel.Model = x;
                    checkableModel.Name = `${moment(x.CreationDate).format("YYYY-MM-DD")} - ${x.FormName}`;
                    checkableModel.Id = x.Id;
                    checkableModel.Checked = false;
                    return checkableModel;
                }) ?? [];
            }));
    }

    public render(): JSX.Element {
        if (!this.state)
            return null;

        return (
            <Modal
                showOverlay
                disableOverlayClose
                header={this.renderHeader()}
                width={650}
                onClose={() => this.props.onCancel && this.props.onCancel()}
                footer={this.renderFooter()}
            >
                <p style={{ padding: "5px" }}>{cacheStore.GetString("SelectPreviousObstetricalPdfLabel")}</p>
                <div style={{ maxHeight: "400px", overflowY: "auto", margin: "15px" }}>
                    {this.renderForms()}
                </div>
            </Modal>
        );
    }

    private renderHeader(): JSX.Element {
        return (
            <div className="row" style={{ margin: "10px 0 0 0" }}>
                <div className="col-xs-7 dragHandle">
                    <h3 className="thin-font">{cacheStore.GetString("PreviousPregnanciesLabel")}</h3>
                </div>
            </div>
        );
    }

    private renderForms(): JSX.Element {
        return (
            <div className="checkbox-list">
                <CheckedListControl
                    DataSource={Immutable.List(this.state.checkablePatientForms)}
                    onChange={(isChecked, id) => this.mutateState(s => s.checkablePatientForms = s.checkablePatientForms.mutate(x => x.Checked = isChecked, x => x.Id === id))}
                    sort={false}
                />
            </div>
        );
    }

    private renderFooter(): JSX.Element {
        return (
            <div className="flex-container" style={{ "justifyContent": "flex-end" }}>
                <div style={{ "marginLeft": "5px" }}>
                    <button className="btn btn-default" onClick={() => this.props.onCancel && this.props.onCancel()}>
                        {cacheStore.GetString("CancelLabel")}
                    </button>
                </div>
                <div style={{ "marginLeft": "5px" }}>
                    <button
                        className="btn btn-primary"
                        onClick={() => this.onContinue()}
                    >
                        {cacheStore.GetString("ContinueLabel")}
                    </button>
                </div>
            </div>
        );
    }

    private onContinue(): void {
        this.printCheckedForms();

        this.props.onContinue && this.props.onContinue();
    }

    private printCheckedForms(): void {
        const forms = this.state.checkablePatientForms
            .filter(p => p.Checked)
            ?.map(p => p.Model);

        if (!forms)
            return;

        let preloadedExportableFiles = forms.map(form => {
            let exportableFile = RadPdfExportableFileHelper.fromPatientForm(form);
            return exportableFile.getFetchUrlDtoAsync();
        });

        Promise.all(preloadedExportableFiles).then(result => {
            let request = {
                fetchUrls: result.toHash(p => p.FetchUrl, p => p.OriginalExportableFile.description),
                addErrorPages: true
            };

            const response = Client.postAsync<PrintFetchUrlResponse>(ServerOperations.Server.Myle_PrintMultipleFetchUrlIntoSingleFile, request);

            response.then(res => {
                if (res.errors.any() || !res.data.HasContent)
                    return;

                PrintHelper.OpenPrintTab(PrintHelper.GetPreviewPath(res.data.FileName));
            });
        });
    }
}

export { PreviousObstetricalPdfFormPopup };
